import { PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { ProjectCard } from '../../components/ProjectCard';
import { SearchAndFilter } from '../../components/SearchAndFilter';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../hooks/useProjects';
import { useProjectStageRouter } from '../../hooks/useProjectStageRouter';
import { generationService } from '../../services/generationService';
import { projectService } from '../../services/projectService';
import { projectStateService } from '../../services/projectStateService';
import { ProjectStatus } from '../../types/project';
import type { ProjectItem } from '../../types/project';
import { ArchitectureReviewScreen } from '../ArchitectureReview';
import './ProjectList.css';

// Constants
const TAB_MY_PROJECTS = 'my-projects';
const TAB_SHARED_PROJECTS = 'shared-projects';
const TAB_APPROVED_PROJECTS = 'approved-projects';
const TAB_PUBLISHED_PROJECTS = 'published-projects';
const TAB_REVIEWS = 'reviews';
const REVIEW_STATUS_APPROVED = 'approved';
const PROJECT_STATUS_PUBLISHED = 'published';
const FILTER_ALL = 'all';
const STATUS_MAP: Record<string, ProjectStatus> = {
  'context_upload': ProjectStatus.CONTEXT_UPLOAD,
  'context_review': ProjectStatus.CONTEXT_REVIEW,
  'adr': ProjectStatus.ADR,
  'generation': ProjectStatus.GENERATION,
  'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
  'in_review': ProjectStatus.IN_REVIEW,
  'completed': ProjectStatus.COMPLETED,
  'published': ProjectStatus.PUBLISHED,
};

type ProjectListTab =
  | typeof TAB_MY_PROJECTS
  | typeof TAB_SHARED_PROJECTS
  | typeof TAB_APPROVED_PROJECTS
  | typeof TAB_PUBLISHED_PROJECTS
  | typeof TAB_REVIEWS;

/**
 * Determine whether a project has been approved by all approvers but is not
 * yet published. These projects live exclusively in the Approved Projects tab.
 */
const isApprovedNotPublished = (project: ProjectItem): boolean =>
  project.review_summary.status === REVIEW_STATUS_APPROVED &&
  project.status !== PROJECT_STATUS_PUBLISHED;

/**
 * ProjectList page component - Enhanced Landing/Dashboard Screen
 * Displays projects in a card-based layout with search, filtering, and progress indicators
 * @return JSX element for the project list dashboard
 */
export const ProjectList: React.FC = () => {
  const {
    projects,
    loading,
    error,
    deleteProject,
    publishProject,
    clearError,
  } = useProjects();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { navigateToProjectStage } = useProjectStageRouter();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial tab from URL query parameter
  const tabFromUrl = searchParams.get('tab') as ProjectListTab | null;
  
  // Local state for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus | 'all'>(FILTER_ALL);
  const [activeTab, setActiveTab] = useState<ProjectListTab>(tabFromUrl ?? TAB_MY_PROJECTS);

  // Published projects are visible to every Archie user and fetched separately.
  const [publishedProjects, setPublishedProjects] = useState<ProjectItem[]>([]);
  const [publishedLoading, setPublishedLoading] = useState<boolean>(false);

  // Shared projects (where the current user is a co-owner but not the owner)
  // are fetched from a dedicated endpoint so only other people's projects
  // appear here, never the user's own projects.
  const [sharedProjects, setSharedProjects] = useState<ProjectItem[]>([]);
  const [sharedLoading, setSharedLoading] = useState<boolean>(false);

  // Update active tab when URL changes (browser back/forward)
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Fetch published projects (visible to all users) when that tab is active.
  useEffect(() => {
    if (activeTab !== TAB_PUBLISHED_PROJECTS) {
      return;
    }
    let cancelled = false;
    const loadPublished = async (): Promise<void> => {
      setPublishedLoading(true);
      try {
        const result = await projectService.getPublishedProjects();
        if (!cancelled) {
          setPublishedProjects(result);
        }
      } catch {
        if (!cancelled) {
          setPublishedProjects([]);
        }
      } finally {
        if (!cancelled) {
          setPublishedLoading(false);
        }
      }
    };
    void loadPublished();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // Fetch shared projects (shared with the user by others) when the Shared
  // Projects tab is active, or when the My Projects tab is active so the
  // dashboard statistics can show the shared project count.
  useEffect(() => {
    if (
      activeTab !== TAB_SHARED_PROJECTS &&
      activeTab !== TAB_MY_PROJECTS
    ) {
      return;
    }
    let cancelled = false;
    const loadShared = async (): Promise<void> => {
      setSharedLoading(true);
      try {
        const result = await projectService.getSharedProjects();
        if (!cancelled) {
          setSharedProjects(result);
        }
      } catch {
        if (!cancelled) {
          setSharedProjects([]);
        }
      } finally {
        if (!cancelled) {
          setSharedLoading(false);
        }
      }
    };
    void loadShared();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  /**
   * Handle tab change - updates both state and URL for browser history support
   */
  const handleTabChange = (tab: ProjectListTab): void => {
    setActiveTab(tab);
    if (tab === TAB_MY_PROJECTS) {
      // Remove tab parameter for default tab
      setSearchParams({});
    } else {
      // Add tab parameter to URL
      setSearchParams({ tab });
    }
  };

  /**
   * Handle creating a new project - navigates to project creation screen
   */
  const handleCreateProject = (): void => {
    navigate('/create');
  };

  /**
   * Handle continuing work on a project using smart routing with state persistence
   */
  const handleContinueProject = async (projectId: number): Promise<void> => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      try {
        const statusEnum = STATUS_MAP[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;

        // Try to get saved state to determine the best route
        try {
          await projectStateService.getProjectState(projectId);
          const lastPageRoute = await projectStateService.getLastPageRoute(
            projectId, statusEnum,
          );
          navigate(lastPageRoute);
        } catch (stateError) {
          // If no saved state, initialize it and use default routing
          await projectStateService.initializeProjectState(project);
          navigateToProjectStage(projectId, statusEnum);
        }
      } catch (_error) {
        // Fallback to default routing
        const statusEnum = STATUS_MAP[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
        navigateToProjectStage(projectId, statusEnum);
      }
    }
  };

  /**
   * Handle deleting a project with confirmation
   */
  const handleDeleteProject = (projectId: number): void => {
    if (globalThis.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      deleteProject(projectId);
    }
  };

  /**
   * Handle opening a project document in the markdown editor
   */
  const handleOpenDocument = (projectId: number): void => {
    navigate(`/project/${projectId}/review?tab=document`);
  };

  /**
   * Download a project's latest final document as a Word (.docx) file.
   */
  const handleDownloadDocument = async (projectId: number): Promise<void> => {
    try {
      const blob = await generationService.exportDocument(projectId, 'docx');
      const project = displayProjects.find((item) => item.id === projectId);
      const safeTitle = (project?.title ?? 'document')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeTitle}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      globalThis.alert('Failed to download the document. Please try again.');
    }
  };

  /**
   * Handle publishing an approved project.
   */
  const handlePublishProject = async (projectId: number): Promise<void> => {
    if (!globalThis.confirm('Publish this approved project?')) {
      return;
    }

    try {
      await publishProject(projectId);
    } catch (_error) {
      globalThis.alert('Failed to publish project. Please try again.');
    }
  };

  /**
   * Clear all search and filter values
   */
  const handleClearFilters = (): void => {
    setSearchTerm('');
    setSelectedStatus(FILTER_ALL);
  };

  // Filter and search projects
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Status filter
      const matchesStatus = selectedStatus === FILTER_ALL || project.status === selectedStatus;
      
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, selectedStatus]);

  // Approved projects (approved by all approvers, not yet published) live
  // exclusively in the Approved Projects tab.
  const approvedProjects = useMemo(
    () => filteredProjects.filter(isApprovedNotPublished),
    [filteredProjects],
  );

  // My Projects shows every project owned by the current user (shared
  // projects appear in their own tab). All owned projects remain here
  // regardless of status, including approved and published ones; the
  // Approved and Published tabs are additional views of the same projects.
  const activeWorkProjects = useMemo(
    () =>
      filteredProjects.filter(
        (project) =>
          !user || !project.owner || project.owner === user.username,
      ),
    [filteredProjects, user],
  );

  // Shared projects come from a dedicated endpoint (other people's projects
  // only). Apply the same search and status filters used for the grid.
  const filteredSharedProjects = useMemo(() => {
    return sharedProjects.filter((project) => {
      const matchesSearch =
        searchTerm === '' ||
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus =
        selectedStatus === FILTER_ALL || project.status === selectedStatus;
      return (
        matchesSearch &&
        matchesStatus &&
        !isApprovedNotPublished(project) &&
        project.status !== PROJECT_STATUS_PUBLISHED
      );
    });
  }, [sharedProjects, searchTerm, selectedStatus]);

  // Published projects filtered by the search term only (status filter does
  // not apply because they are all published).
  const filteredPublishedProjects = useMemo(() => {
    if (searchTerm === '') {
      return publishedProjects;
    }
    const term = searchTerm.toLowerCase();
    return publishedProjects.filter(
      (project) =>
        project.title.toLowerCase().includes(term) ||
        (project.description?.toLowerCase().includes(term)),
    );
  }, [publishedProjects, searchTerm]);

  // The list of projects rendered in the grid depends on the active tab.
  const displayProjects = useMemo(() => {
    switch (activeTab) {
      case TAB_APPROVED_PROJECTS:
        return approvedProjects;
      case TAB_PUBLISHED_PROJECTS:
        return filteredPublishedProjects;
      case TAB_SHARED_PROJECTS:
        return filteredSharedProjects;
      default:
        // My Projects renders the user's own active-work projects.
        return activeWorkProjects;
    }
  }, [
    activeTab,
    approvedProjects,
    filteredPublishedProjects,
    filteredSharedProjects,
    activeWorkProjects,
  ]);

  const isPublishedTabLoading =
    activeTab === TAB_PUBLISHED_PROJECTS && publishedLoading;
  const isSharedTabLoading =
    activeTab === TAB_SHARED_PROJECTS && sharedLoading;
  const isAnyProjectsLoading =
    loading || isPublishedTabLoading || isSharedTabLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto w-full px-4 py-8 max-w-[1600px]">
        {/* Header Section */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Projects
              </h1>
              <p className="text-sm text-gray-600">
                Create and manage solution architectures with AI assistance
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#336094] hover:bg-[#6e94c0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0097a9] transition-all duration-200 gap-2 main-button"
                onClick={handleCreateProject}
              >
                <div className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full">
                  <SparklesIcon className="w-3 h-3" />
                </div>
                Create New Project
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabChange(TAB_MY_PROJECTS)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === TAB_MY_PROJECTS
                    ? 'border-[#336094] text-[#336094]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Projects
              </button>
              <button
                onClick={() => handleTabChange(TAB_SHARED_PROJECTS)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === TAB_SHARED_PROJECTS
                    ? 'border-[#336094] text-[#336094]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Shared Projects
              </button>
              <button
                onClick={() => handleTabChange(TAB_REVIEWS)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === TAB_REVIEWS
                    ? 'border-[#336094] text-[#336094]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Reviews
              </button>
              <button
                onClick={() => handleTabChange(TAB_APPROVED_PROJECTS)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === TAB_APPROVED_PROJECTS
                    ? 'border-[#336094] text-[#336094]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Approved Projects
              </button>
              <button
                onClick={() => handleTabChange(TAB_PUBLISHED_PROJECTS)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === TAB_PUBLISHED_PROJECTS
                    ? 'border-[#336094] text-[#336094]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Published Projects
              </button>
            </nav>
          </div>

          {/* Search and Filter - Only show for My Projects tab */}
          {activeTab === TAB_MY_PROJECTS && (
            <SearchAndFilter
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              onClearFilters={handleClearFilters}
            />
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <ErrorMessage 
              message={error} 
              onDismiss={clearError}
            />
          </div>
        )}

        {/* Loading State */}
        {isAnyProjectsLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner label="Loading projects..." />
          </div>
        )}

        {/* Content based on active tab */}
        {!isAnyProjectsLoading && (
          <>
            {activeTab !== TAB_REVIEWS ? (
              <>
                {/* Project Grid - Show for project list tabs */}
                {displayProjects.length > 0 ? (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(min(20rem,100%),1fr))] gap-6">
                    {displayProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        onContinue={handleContinueProject}
                        onDelete={handleDeleteProject}
                        onOpenDocument={handleOpenDocument}
                        onDownloadDocument={handleDownloadDocument}
                        onPublish={handlePublishProject}
                        isInSharedTab={activeTab === TAB_SHARED_PROJECTS}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mb-4">
                      <SparklesIcon className="w-16 h-16 mx-auto text-gray-300" />
                    </div>
                    {(() => {
                      const hasFilters = Boolean(searchTerm ?? '') || selectedStatus !== FILTER_ALL;
                      if (hasFilters) {
                        return (
                          <>
                            <h3 className="text-xl font-semibold text-gray-500 mb-2">
                              No projects match your filters
                            </h3>
                            <p className="text-gray-400 mb-6 max-w-md mx-auto">
                              Try adjusting your search term or filters to find projects.
                            </p>
                          </>
                        );
                      }
                      if (activeTab === TAB_APPROVED_PROJECTS) {
                        return (
                          <>
                            <h3 className="text-xl font-semibold text-gray-500 mb-2">
                              No approved projects yet
                            </h3>
                            <p className="text-gray-400 mb-6 max-w-md mx-auto">
                              Projects approved by all approvers appear here, ready to publish.
                            </p>
                          </>
                        );
                      }
                      if (activeTab === TAB_PUBLISHED_PROJECTS) {
                        return (
                          <>
                            <h3 className="text-xl font-semibold text-gray-500 mb-2">
                              No published projects yet
                            </h3>
                            <p className="text-gray-400 mb-6 max-w-md mx-auto">
                              Published projects are visible to everyone and will appear here.
                            </p>
                          </>
                        );
                      }
                      const isSharedTab = activeTab === TAB_SHARED_PROJECTS;
                      return (
                        <>
                          <h3 className="text-xl font-semibold text-gray-500 mb-2">
                            {isSharedTab ? 'No shared projects yet' : 'No projects yet'}
                          </h3>
                          <p className="text-gray-400 mb-6 max-w-md mx-auto">
                            {isSharedTab
                              ? 'When someone shares a project with you, it will appear here.'
                              : 'Get started by creating your first architecture project with AI assistance.'}
                          </p>
                        </>
                      );
                    })()}
                    {!searchTerm && selectedStatus === FILTER_ALL && activeTab === TAB_MY_PROJECTS && (
                      <button
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#336094] hover:bg-[#6e94c0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0097a9] transition-colors gap-2 main-button"
                        onClick={handleCreateProject}
                      >
                        <SparklesIcon className="w-5 h-5" />
                        Create Your First Project
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Reviews Tab - Architecture Review Workflow */
              <ArchitectureReviewScreen />
            )}
          </>
        )}

        {/* Project Statistics - Only show for My Projects tab */}
        {!loading && projects.length > 0 && activeTab === TAB_MY_PROJECTS && (
          <div className="mt-12 bg-white rounded-lg shadow overflow-hidden">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(11rem,100%),1fr))] gap-px bg-gray-200">
              <div className="px-6 py-4 bg-white">
                <div className="text-sm font-medium text-gray-500">Total Projects</div>
                <div className="text-2xl font-bold text-[#336094]">{projects.length}</div>
              </div>
              <div className="px-6 py-4 bg-white">
                <div className="text-sm font-medium text-gray-500">Shared Projects</div>
                <div className="text-2xl font-bold text-[#7c4dc4]">
                  {sharedProjects.length}
                </div>
              </div>
              <div className="px-6 py-4 bg-white">
                <div className="text-sm font-medium text-gray-500">Completed</div>
                <div className="text-2xl font-bold text-[#008557]">
                  {
                    projects.filter(
                      (p) =>
                        p.status === ProjectStatus.COMPLETED ||
                        p.status === ProjectStatus.PUBLISHED,
                    ).length
                  }
                </div>
              </div>
              <div className="px-6 py-4 bg-white">
                <div className="text-sm font-medium text-gray-500">In Progress</div>
                <div className="text-2xl font-bold text-[#ffb15b]">
                  {projects.filter(p => 
                    p.status !== ProjectStatus.COMPLETED && 
                    p.status !== ProjectStatus.PUBLISHED,
                  ).length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
